/**
* Copyright by: 

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/

import { getImageSliderFromTaskDatum } from '../../../app/shared/functions';
import { IconDefinition, faSquareList } from '@fortawesome/pro-regular-svg-icons';
import { TaskDataArray, TaskDataObject } from '../../task.model';
import { RecordBaseModel, WorkflowItemSubtype, WorkflowItemType } from '../record-base.model';
import { FormInputType, getIcon, getInputType } from './input-record.model';

export enum FormElementType {
  TEXT = 'TEXT',
  TEXTFIELD = 'TEXTFIELD',
  CHECKBOX = 'CHECKBOX',
  RADIOGROUP = 'RADIOGROUP',
  DROPDOWN = 'DROPDOWN',
  IMAGE = 'IMAGE'
}

interface FormElement {
  elementType: FormElementType;
  elementRecord: any;
}

export class FormRecord extends RecordBaseModel {
  itemIcon: IconDefinition = faSquareList;
  itemData?: FormRecordData;

  constructor(itemName: string) {
    super(itemName, WorkflowItemType.FORM, WorkflowItemSubtype.UNKNOWN);
  }

  fromJson(json: any): FormRecord | undefined {
    if (!json) return;
    const record = new FormRecord(json.itemName.value);
    record.itemData = FormRecordData.fromJson(json);
    return record;
  }
}

export class FormRecordData {
  formElements?: FormElement[];

  constructor(formElements: FormElement[] = []) {
    this.formElements = formElements;
  }

  static fromJson(json: any): FormRecordData | undefined {
    const formElements: FormElement[] = [];

    if (json.formData?.type === 'ARRAY') {
      let taskDataArrayModel = Object.create(TaskDataArray.prototype);
      let taskDataArray = taskDataArrayModel.fromJson(json.formData) as TaskDataArray;
      taskDataArray.value.forEach((item) => {
        if (item instanceof TaskDataObject) {
          const type = item.value.get('type')?.value;
          const value = item.value.get('value');
          const elementRecord = FormRecordData.createElementRecord(type, item);
          if (elementRecord) {
            formElements.push({ elementType: type, elementRecord });
          }
          if (type === FormElementType.IMAGE && value) {
            const text = item.value.get('text')?.value || $localize`:Image@@image:Image`;
            const image = getImageSliderFromTaskDatum(value);

            formElements.push({
              elementType: FormElementType.IMAGE,
              elementRecord: { image: image, text: text }
            });
          }
        }
      });
    }

    return new FormRecordData(formElements);
  }

  private static createElementRecord(type: string, item: TaskDataObject): any {
    switch (type) {
      case FormElementType.TEXT:
        return TextRecord.fromJson(item);
      case FormElementType.TEXTFIELD:
        return TextFieldRecord.fromJson(item);
      case FormElementType.CHECKBOX:
        return CheckboxRecord.fromJson(item);
      case FormElementType.RADIOGROUP:
        return RadiogroupRecord.fromJson(item);
      case FormElementType.DROPDOWN:
        return DropdownRecord.fromJson(item);
      default:
        return undefined;
    }
  }
}

export class TextRecord {
  displayedText: string;

  constructor(displayedText: string) {
    this.displayedText = displayedText;
  }

  static fromJson(item: any): TextRecord | undefined {
    const text = item.value.get('text')?.value || '';
    return new TextRecord(text);
  }
}

export class TextFieldRecord {
  label: string;
  inputValue: string;
  inputType: FormInputType;
  icon: IconDefinition;

  constructor(label: string, inputValue: string, inputType: FormInputType, icon: IconDefinition) {
    this.label = label;
    this.inputValue = inputValue;
    this.inputType = inputType;
    this.icon = icon;
  }

  static fromJson(item: any): TextFieldRecord | undefined {
    const label = item.value.get('text')?.value || $localize`:Textfield@@textfield:Textfield`;
    const inputValue = item.value.get('value')?.value || '';
    const inputType = getInputType(item.value.get('inputType')?.value || '');
    const icon = getIcon(inputType);
    return new TextFieldRecord(label, inputValue, inputType, icon);
  }
}

export class CheckboxRecord {
  displayedText: string;
  isSelected: boolean;

  constructor(displayedText: string, isSelected: boolean) {
    this.displayedText = displayedText;
    this.isSelected = isSelected;
  }

  static fromJson(item: any): CheckboxRecord {
    const displayedText = item.value.get('text')?.value || '';
    const isSelected = item.value.get('value')?.value || false;
    return new CheckboxRecord(displayedText, isSelected);
  }
}

export class RadiogroupRecord {
  displayedText: string;
  options?: OptionsRecord[];

  constructor(displayedText: string, options: OptionsRecord[] = []) {
    this.displayedText = displayedText;
    this.options = options;
  }

  static fromJson(item: any): RadiogroupRecord {
    const displayedText =
      item.value.get('text')?.value || $localize`:Radio Group@@radioGroup:Radio group`;
    const optionsArray = item.value.get('options')?.value || [];
    const selectedOption = item.value.get('value')?.value;
    const options = optionsArray.map(
      (option: any) => new OptionsRecord(option.value || '', option.value === selectedOption)
    );
    return new RadiogroupRecord(displayedText, options);
  }
}

export class DropdownRecord {
  displayedText: string;
  options?: OptionsRecord[];

  constructor(displayedText: string, options: OptionsRecord[] = []) {
    this.displayedText = displayedText;
    this.options = options;
  }

  static fromJson(item: any): DropdownRecord {
    const displayedText = item.value.get('text')?.value || $localize`:Dropdown@@dropdown:Dropdown`;
    const optionsArray = item.value.get('options')?.value || [];
    const selectedOption = item.value.get('value')?.value;
    const options = optionsArray.map(
      (option: any) => new OptionsRecord(option.value || '', option.value === selectedOption)
    );
    return new DropdownRecord(displayedText, options);
  }
}

class OptionsRecord {
  optionText: string;
  isSelected: boolean;

  constructor(optionText: string, isSelected: boolean) {
    this.optionText = optionText;
    this.isSelected = isSelected;
  }
}
